import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Barcode,
  CalendarPlus,
  CheckCircle2,
  ClipboardList,
  Eye,
  FileText,
  FlaskConical,
  HelpCircle,
  Microscope,
  ScanLine,
  Send,
  TestTube2,
  Truck,
  UploadCloud,
  UserPlus,
  Users,
} from 'lucide-react';

interface GuideStep {
  title: string;
  description: string;
  location: string;
  actionLabel: string;
  path: string;
  Icon: React.ElementType;
  color: string;
}

interface LabProcessStep {
  title: string;
  description: string;
  role: string;
  action: string;
  Icon: React.ElementType;
  color: string;
}

const WORKFLOW_STEPS: GuideStep[] = [
  {
    title: 'Register the patient',
    description:
      'Create the patient profile and verify the patient name, MRN, contact details, and demographics.',
    location: 'Patients → Patient Register',
    actionLabel: 'Open Patients',
    path: '/patients-history',
    Icon: UserPlus,
    color: '#1D6FB8',
  },
  {
    title: 'Schedule a pathology order',
    description:
      'Add an order for the patient, select Pathology as the appointment type, choose one or more lab tests, then save.',
    location: 'Scheduling → Add Order',
    actionLabel: 'Open Scheduling',
    path: '/scheduling',
    Icon: CalendarPlus,
    color: '#7C3AED',
  },
  {
    title: 'Track the linked order',
    description:
      'The saved pathology appointment creates a linked entry in both the Slide Library and Lab Requests.',
    location: 'Pathology → Slide Library / Lab → Lab Requests',
    actionLabel: 'Open Lab Requests',
    path: '/lab/orders',
    Icon: ClipboardList,
    color: '#D97706',
  },
  {
    title: 'Review the completed case',
    description:
      'Follow the laboratory status, open the report when it is ready, and open the slide viewer when a digital slide has been imported.',
    location: 'Pathology → Slide Library',
    actionLabel: 'Open Slide Library',
    path: '/pathology/slides',
    Icon: Microscope,
    color: '#059669',
  },
];

const STATUS_ITEMS = [
  {
    label: 'Awaiting Sample',
    description: 'The order exists, but specimen collection has not been completed.',
    color: '#92400E',
    background: 'rgba(234,179,8,0.10)',
  },
  {
    label: 'Sample Collected',
    description: 'The specimen has been registered and the laboratory workflow can continue.',
    color: '#4F46E5',
    background: 'rgba(79,70,229,0.10)',
  },
  {
    label: 'Slide Ready',
    description: 'A digital whole-slide image is available for viewing.',
    color: '#059669',
    background: 'rgba(5,150,105,0.10)',
  },
  {
    label: 'Report Ready',
    description:
      'The laboratory report is available. This does not automatically mean a digital slide is available.',
    color: '#1D6FB8',
    background: 'rgba(29,111,184,0.10)',
  },
];

const LAB_PROCESS_STEPS: LabProcessStep[] = [
  {
    title: 'Open the lab request',
    description:
      'Find the linked patient order, open Order Details, and confirm every ordered test before processing.',
    role: 'Doctor / Lab team',
    action: 'Lab → Lab Requests',
    Icon: ClipboardList,
    color: '#1D6FB8',
  },
  {
    title: 'Register each specimen',
    description:
      'For every ordered test, select the specimen type and container, enter quantity and collector details, then register it. A barcode is created for identification.',
    role: 'Technician / Nurse',
    action: 'Register Specimen',
    Icon: Barcode,
    color: '#7C3AED',
  },
  {
    title: 'Collect the specimen',
    description:
      'Use Mark Collected after confirming patient identity, label, specimen type, container, and quantity.',
    role: 'Technician / Nurse',
    action: 'Pending → Collected',
    Icon: TestTube2,
    color: '#D97706',
  },
  {
    title: 'Transit and accession',
    description:
      'Send the specimen for transit, then accession it when the laboratory receives and accepts it for testing.',
    role: 'Technician / Nurse',
    action: 'Collected → In Transit → Accessioned',
    Icon: Truck,
    color: '#0C7F99',
  },
  {
    title: 'Enter and verify results',
    description:
      'Record parameters, values, units, reference ranges, findings, and impression. Verify the draft, then submit it for review.',
    role: 'Technician / Pathologist',
    action: 'Draft → Pending Approval',
    Icon: ScanLine,
    color: '#4F46E5',
  },
  {
    title: 'Approve or reject',
    description:
      'Review the submitted result and its clinical details. Approve it, or reject it with a reason so it can be corrected and resubmitted.',
    role: 'Pathologist',
    action: 'Pending Approval → Approved',
    Icon: CheckCircle2,
    color: '#059669',
  },
  {
    title: 'Generate and deliver report',
    description:
      'After the required results are approved, generate the combined order PDF. Review it before printing or emailing it to the intended recipient.',
    role: 'Pathologist / Admin',
    action: 'View PDF → Print / Email',
    Icon: Send,
    color: '#0F766E',
  },
];

const StepCard: React.FC<{ step: GuideStep; number: number; onOpen: () => void }> = ({
  step,
  number,
  onOpen,
}) => {
  const StepIcon = step.Icon;

  return (
    <article
      className="relative flex min-h-[230px] flex-col overflow-hidden rounded-2xl border p-5"
      style={{
        background: 'var(--v2-bg-card)',
        borderColor: 'var(--v2-border-card)',
        boxShadow: 'var(--v2-shadow-card)',
      }}
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: step.color }}
      />

      <div className="mb-4 flex items-center justify-between">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${step.color}16`, color: step.color }}
        >
          <StepIcon
            className="h-5 w-5"
            strokeWidth={2}
          />
        </span>
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-black"
          style={{ background: `${step.color}16`, color: step.color }}
          aria-label={`Step ${number}`}
        >
          {number}
        </span>
      </div>

      <h2 className="m-0 text-[15px] font-black text-[var(--v2-text-heading)]">{step.title}</h2>
      <p className="m-0 mt-2 flex-1 text-[12px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
        {step.description}
      </p>
      <p className="m-0 mt-3 text-[10px] font-bold uppercase tracking-wider text-[var(--v2-text-muted)]">
        {step.location}
      </p>

      <button
        type="button"
        onClick={onOpen}
        className="mt-4 inline-flex h-9 items-center justify-center gap-2 rounded-xl border-0 px-3 text-[12px] font-bold text-white transition-opacity hover:opacity-90 active:scale-[0.98]"
        style={{ background: step.color }}
      >
        {step.actionLabel}
        <ArrowRight
          className="h-3.5 w-3.5"
          strokeWidth={2.5}
        />
      </button>
    </article>
  );
};

export const PathologyUserGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="v2-animate-fade-in flex flex-col gap-4 pb-6">
      <section
        className="relative overflow-hidden rounded-2xl border px-5 py-6 sm:px-7"
        style={{
          background: 'var(--v2-bg-card)',
          borderColor: 'var(--v2-border-glass-strong)',
          boxShadow: 'var(--v2-shadow-card)',
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'var(--v2-bg-hero)' }}
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <span
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl"
              style={{ background: 'rgba(29,111,184,0.10)', color: '#1D6FB8' }}
            >
              <HelpCircle
                className="h-6 w-6"
                strokeWidth={2}
              />
            </span>
            <div>
              <p className="m-0 text-[10px] font-black uppercase tracking-[0.18em] text-[var(--v2-primary-500)]">
                Doctor and laboratory workflow
              </p>
              <h1 className="m-0 mt-1 text-xl font-black text-[var(--v2-text-heading)] sm:text-2xl">
                Pathology User Guide
              </h1>
              <p className="m-0 mt-2 max-w-3xl text-[13px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
                Register the patient, schedule the pathology tests, track the linked laboratory
                order, complete specimen and result processing, and review the final report or
                digital slide.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/pathology/slides')}
            className="inline-flex h-10 flex-shrink-0 items-center justify-center gap-2 rounded-xl border-0 bg-[var(--v2-primary-500)] px-4 text-[12px] font-bold text-white hover:opacity-90"
          >
            <Microscope
              className="h-4 w-4"
              strokeWidth={2}
            />
            Go to Slide Library
          </button>
        </div>
      </section>

      <section aria-labelledby="pathology-workflow-heading">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2
              id="pathology-workflow-heading"
              className="m-0 text-[16px] font-black text-[var(--v2-text-heading)]"
            >
              End-to-end workflow
            </h2>
            <p className="m-0 mt-1 text-[12px] text-[var(--v2-text-muted)]">
              Follow these steps in order for every new pathology request.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {WORKFLOW_STEPS.map((step, index) => (
            <StepCard
              key={step.title}
              step={step}
              number={index + 1}
              onOpen={() => navigate(step.path)}
            />
          ))}
        </div>
      </section>

      <section
        aria-labelledby="lab-process-heading"
        className="rounded-2xl border p-5"
        style={{
          background: 'var(--v2-bg-card)',
          borderColor: 'var(--v2-border-card)',
          boxShadow: 'var(--v2-shadow-card)',
        }}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="m-0 text-[10px] font-black uppercase tracking-[0.16em] text-[#7C3AED]">
              Detailed operating procedure
            </p>
            <h2
              id="lab-process-heading"
              className="m-0 mt-1 text-[16px] font-black text-[var(--v2-text-heading)]"
            >
              Complete laboratory process
            </h2>
            <p className="m-0 mt-1 max-w-3xl text-[12px] leading-relaxed text-[var(--v2-text-muted)]">
              Open the order from Lab Requests and complete the following sequence for each ordered
              test. Available actions depend on the signed-in user’s role.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/lab/orders')}
            className="inline-flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-xl border-0 bg-[#7C3AED] px-3 text-[11px] font-bold text-white hover:opacity-90"
          >
            <ClipboardList className="h-3.5 w-3.5" />
            Open Lab Requests
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {LAB_PROCESS_STEPS.map((step, index) => {
            const ProcessIcon = step.Icon;
            return (
              <article
                key={step.title}
                className="relative flex flex-col rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{ background: `${step.color}16`, color: step.color }}
                  >
                    <ProcessIcon
                      className="h-[18px] w-[18px]"
                      strokeWidth={2}
                    />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-wider text-[var(--v2-text-muted)]">
                    Step {index + 1}
                  </span>
                </div>
                <h3 className="m-0 text-[13px] font-black text-[var(--v2-text-heading)]">
                  {step.title}
                </h3>
                <p className="m-0 mt-2 flex-1 text-[11px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
                  {step.description}
                </p>
                <div className="mt-3 border-t border-[var(--v2-border-subtle)] pt-3">
                  <p className="m-0 text-[9px] font-black uppercase tracking-wider text-[var(--v2-text-muted)]">
                    Action
                  </p>
                  <p
                    className="m-0 mt-0.5 text-[10px] font-bold"
                    style={{ color: step.color }}
                  >
                    {step.action}
                  </p>
                  <span
                    className="mt-2 inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold"
                    style={{ background: `${step.color}12`, color: step.color }}
                  >
                    {step.role}
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        <div
          className="mt-3 grid grid-cols-1 gap-2 rounded-xl border p-3 md:grid-cols-3"
          style={{
            background: 'rgba(217,119,6,0.05)',
            borderColor: 'rgba(217,119,6,0.18)',
          }}
        >
          <div>
            <p className="m-0 text-[10px] font-black text-[#92400E]">Rejected specimen</p>
            <p className="m-0 mt-1 text-[10px] leading-relaxed text-[var(--v2-text-secondary)]">
              Record the rejection reason. Processing stops until the specimen issue is resolved or
              a replacement is registered.
            </p>
          </div>
          <div>
            <p className="m-0 text-[10px] font-black text-[#92400E]">Rejected result</p>
            <p className="m-0 mt-1 text-[10px] leading-relaxed text-[var(--v2-text-secondary)]">
              Correct the result, verify it again, and resubmit it for pathologist approval.
            </p>
          </div>
          <div>
            <p className="m-0 text-[10px] font-black text-[#92400E]">Before delivery</p>
            <p className="m-0 mt-1 text-[10px] leading-relaxed text-[var(--v2-text-secondary)]">
              Confirm the required ordered tests are approved and verify the recipient before
              printing or emailing the combined report.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <section
          aria-labelledby="linked-records-heading"
          className="rounded-2xl border p-5 xl:col-span-3"
          style={{
            background: 'var(--v2-bg-card)',
            borderColor: 'var(--v2-border-card)',
            boxShadow: 'var(--v2-shadow-card)',
          }}
        >
          <div className="mb-4 flex items-center gap-3">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: 'rgba(124,58,237,0.10)', color: '#7C3AED' }}
            >
              <FlaskConical
                className="h-[18px] w-[18px]"
                strokeWidth={2}
              />
            </span>
            <div>
              <h2
                id="linked-records-heading"
                className="m-0 text-[15px] font-black text-[var(--v2-text-heading)]"
              >
                One order, two operational views
              </h2>
              <p className="m-0 mt-0.5 text-[11px] text-[var(--v2-text-muted)]">
                Both entries refer to the same scheduled pathology request.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] p-4">
              <div className="flex items-center gap-2">
                <Microscope className="h-4 w-4 text-[var(--v2-primary-500)]" />
                <h3 className="m-0 text-[13px] font-black text-[var(--v2-text-heading)]">
                  Slide Library
                </h3>
              </div>
              <p className="m-0 mt-2 text-[12px] leading-relaxed text-[var(--v2-text-secondary)]">
                Use this view to track the patient, appointment, order status, report status, and
                availability of the digital slide.
              </p>
            </div>

            <div className="rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] p-4">
              <div className="flex items-center gap-2">
                <TestTube2 className="h-4 w-4 text-[#D97706]" />
                <h3 className="m-0 text-[13px] font-black text-[var(--v2-text-heading)]">
                  Lab Requests
                </h3>
              </div>
              <p className="m-0 mt-2 text-[12px] leading-relaxed text-[var(--v2-text-secondary)]">
                Use this view for specimen collection, test processing, result review, approval, and
                report delivery according to the user’s role.
              </p>
            </div>
          </div>

          <div
            className="mt-3 flex items-start gap-2 rounded-xl border px-3 py-2.5"
            style={{
              background: 'rgba(29,111,184,0.06)',
              borderColor: 'rgba(29,111,184,0.18)',
            }}
          >
            <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1D6FB8]" />
            <p className="m-0 text-[11px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
              Doctors can create orders and review progress. Specimen collection and result-entry
              actions are shown only to roles that have those permissions.
            </p>
          </div>
        </section>

        <section
          aria-labelledby="status-guide-heading"
          className="rounded-2xl border p-5 xl:col-span-2"
          style={{
            background: 'var(--v2-bg-card)',
            borderColor: 'var(--v2-border-card)',
            boxShadow: 'var(--v2-shadow-card)',
          }}
        >
          <div className="mb-4 flex items-center gap-3">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: 'rgba(5,150,105,0.10)', color: '#059669' }}
            >
              <CheckCircle2
                className="h-[18px] w-[18px]"
                strokeWidth={2}
              />
            </span>
            <div>
              <h2
                id="status-guide-heading"
                className="m-0 text-[15px] font-black text-[var(--v2-text-heading)]"
              >
                Status guide
              </h2>
              <p className="m-0 mt-0.5 text-[11px] text-[var(--v2-text-muted)]">
                What the Slide Library badges mean.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            {STATUS_ITEMS.map(status => (
              <div
                key={status.label}
                className="rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] p-3"
              >
                <span
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-black"
                  style={{ background: status.background, color: status.color }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: status.color }}
                  />
                  {status.label}
                </span>
                <p className="m-0 mt-1.5 text-[11px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
                  {status.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section
        className="flex flex-col gap-3 rounded-2xl border p-5 sm:flex-row sm:items-center sm:justify-between"
        style={{
          background: 'var(--v2-bg-card)',
          borderColor: 'var(--v2-border-card)',
          boxShadow: 'var(--v2-shadow-card)',
        }}
      >
        <div className="flex items-start gap-3">
          <span
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl"
            style={{ background: 'rgba(217,119,6,0.10)', color: '#D97706' }}
          >
            <Eye
              className="h-[18px] w-[18px]"
              strokeWidth={2}
            />
          </span>
          <div>
            <h2 className="m-0 text-[14px] font-black text-[var(--v2-text-heading)]">
              Why can “View Slide” still be disabled?
            </h2>
            <p className="m-0 mt-1 max-w-3xl text-[11px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
              The button becomes available only after the order is linked to an imported digital
              whole-slide image. A ready PDF report and a ready digital slide are separate outcomes.
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <button
            type="button"
            onClick={() => navigate('/lab/orders')}
            className="inline-flex h-9 items-center gap-2 rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] px-3 text-[11px] font-bold text-[var(--v2-text-primary)]"
          >
            <FileText className="h-3.5 w-3.5" />
            Lab Requests
          </button>
          <button
            type="button"
            onClick={() => navigate('/pathology/slides')}
            className="inline-flex h-9 items-center gap-2 rounded-xl border-0 bg-[var(--v2-primary-500)] px-3 text-[11px] font-bold text-white"
          >
            <UploadCloud className="h-3.5 w-3.5" />
            Slide Library
          </button>
        </div>
      </section>
    </div>
  );
};

export default PathologyUserGuide;
